import { FadeUp, MaskLine } from "./Reveal";

const BENEFITS = [
  ["Physically", "Unwound and open"],
  ["Mentally", "Clear and present"],
  ["Emotionally", "Lifted — blissful, high on life, and ready to take on anything"],
];

const PERFECT_FOR = ["Corporate retreats", "Team wellness days", "Lunch-and-learn sessions", "Any moment your team needs to come back to themselves"];

export const Euphoria = () => (
  <section id="euphoria" className="relative py-24 lg:py-32 overflow-hidden" data-testid="euphoria-section">
    <div className="spotlight pointer-events-none absolute top-1/3 right-[-15%] h-[40rem] w-[40rem] rounded-full" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
      <div>
        <FadeUp>
          <p className="eyebrow flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-gold/60" aria-hidden="true" />
            The Restorative Experience — Euphoria
          </p>
        </FadeUp>

        <h2 className="mt-8 font-serif font-light tracking-[-0.02em] leading-[1.02] text-ivory text-[clamp(2.6rem,6vw,4.8rem)]" data-testid="euphoria-headline">
          <MaskLine>One hour.</MaskLine>
          <MaskLine delay={0.12}>Fully unwound.</MaskLine>
          <MaskLine delay={0.24}>
            <em className="italic text-gold">High on life.</em>
          </MaskLine>
        </h2>

        <div className="mt-9 space-y-5 text-mute text-base sm:text-lg leading-relaxed">
          <FadeUp>
            <p>
              <span className="text-ivory font-medium">Euphoria</span> is our signature restorative
              group session — a one-hour journey designed to unwind the body, quiet the mind, and
              elevate the spirit. Rooted in fascia science, this experience weaves together guided
              stretching, breathwork, and meditation into one seamless flow that targets the
              connective tissue holding your team's stress and tension.
            </p>
          </FadeUp>
          <FadeUp>
            <p>
              Fascia is the body's web — it connects everything. When it is tight, we feel it
              everywhere: in our posture, our energy, our mood. <span className="text-ivory">Euphoria
              releases that grip.</span>
            </p>
          </FadeUp>
        </div>

        <FadeUp delay={0.05}>
          <p className="mt-9 eyebrow">In just one hour, your team will feel</p>
        </FadeUp>
        <div className="mt-5 space-y-0">
          {BENEFITS.map(([t, d], i) => (
            <FadeUp key={t} delay={i * 0.07}>
              <div className="flex items-baseline gap-5 border-b border-white/5 py-4" data-testid={`euphoria-benefit-${i + 1}`}>
                <span className="w-32 shrink-0 font-serif text-2xl text-gold font-light italic">{t}</span>
                <span className="text-sm sm:text-base text-ivory/85 leading-relaxed">{d}</span>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.1}>
          <p className="mt-8 text-mute text-base sm:text-lg leading-relaxed">
            This is not a traditional yoga or fitness class. Euphoria is a full mind, body, and soul
            reset — leaving your team <span className="text-ivory">proactive, connected, and genuinely
            loving their work and their life.</span>
          </p>
        </FadeUp>

        <FadeUp delay={0.12}>
          <p className="mt-8 font-mono text-[9px] uppercase tracking-[0.2em] text-mute">Perfect for</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {PERFECT_FOR.map((p) => (
              <span key={p} className="rounded-full border border-white/10 px-4 py-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-ivory/70">
                {p}
              </span>
            ))}
          </div>
        </FadeUp>

        <FadeUp delay={0.15}>
          <a
            href="mailto:akamba81@gmail.com?subject=Euphoria%20for%20My%20Team"
            data-testid="euphoria-cta"
            className="mt-10 inline-flex items-center gap-3 rounded-full bg-gold px-10 py-4 font-mono text-[11px] uppercase tracking-[0.18em] font-medium text-ink transition-all duration-300 hover:bg-ivory hover:shadow-[0_0_54px_rgba(212,175,55,0.45)]"
          >
            Book Euphoria for Your Team →
          </a>
          <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.16em] text-mute/70">
            One practitioner · Full group guided · One hour
          </p>
        </FadeUp>
      </div>

      <FadeUp delay={0.1}>
        <div className="relative">
          <div className="clip-notch relative overflow-hidden border border-white/10 shadow-[0_30px_90px_rgba(212,175,55,0.12)]" data-testid="euphoria-poster">
            <img
              src="/images/euphoria-poster.png"
              alt="The Euphoria Experience — fascia stretch, breathwork, meditation. Unwind. Embody. Transcend."
              loading="lazy"
              className="w-full h-auto transition-transform duration-700 hover:scale-[1.02]"
            />
          </div>
          <div className="glass-chip absolute -bottom-4 left-6 right-6 rounded-full px-5 py-2.5 text-center font-mono text-[9px] uppercase tracking-[0.24em] text-gold sm:hidden">
            A 1 hour journey to higher vibes
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
);
